import {ReactElement, useEffect, useRef, useState} from "react";
import {styled} from "styled-components";

import IconTooltips from "@/assets/iconTooltips.svg?react";
import {quicksandMedium} from "@/GlobalStyle";
import {breakpoints} from "@/GlobalStyle";

const TooltipsContent = styled.div`
  position: relative;
  width: 1.25rem;
  height: 1.25rem;
  border: solid 2px ${({theme}) => theme.colors.mediumSecondary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  flex: none;

  justify-content: center;
  &:hover {
    background-color: ${({theme}) => theme.colors.mediumSecondary};
    cursor: pointer;
    & svg {
      fill: white;
    }
  }
  @media (max-width: ${breakpoints.md}) {
    width: 1rem;
    height: 1rem;
  }
`;

const IconTooltipsStyled = styled(IconTooltips)`
  max-width: 80%;
  max-height: 80%;
  width: auto;
  fill: ${({theme}) => theme.colors.mediumSecondary};
  margin: 0 auto;
`;

const TooltipsMessage = styled.div<{
  $isVisible: boolean;
  $adjustedLeft: string | null;
  $adjustedRight: string | null;
  $adjustedTop: string | null;
  $adjustedBottom: string | null;
}>`
  user-select: none;
  position: absolute;
  max-width: 60vw;
  width: max-content;
  left: ${({$adjustedLeft}) => $adjustedLeft};
  right: ${({$adjustedRight}) => $adjustedRight};
  top: ${({$adjustedTop}) => $adjustedTop};
  bottom: ${({$adjustedBottom}) => $adjustedBottom};
  border: 2px solid ${({theme}) => theme.colors.darkPrimary};
  box-shadow: 0 0 6px 0 ${({theme}) => theme.colors.shadowBloc};
  background-color: ${({theme}) => theme.colors.darkPrimary};
  padding: 0.5rem;
  display: ${({$isVisible}) => ($isVisible ? "block" : "none")};
  color: white;
  font-size: 0.875rem;
  ${quicksandMedium}
`;

export function Tooltips({content}: {content: string}): ReactElement {
  const [visible, setVisible] = useState(false);
  const [adjustedLeft, setAdjustedLeft] = useState<string | null>(null);
  const [adjustedRight, setAdjustedRight] = useState<string | null>(null);
  const [adjustedTop, setAdjustedTop] = useState<string | null>(null);
  const [adjustedBottom, setAdjustedBottom] = useState<string | null>("25px");
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setAdjustedLeft(null);
    setAdjustedRight(null);
    setAdjustedTop(null);
    setAdjustedBottom("25px");
    if (!visible || !tooltipRef.current || !triggerRef.current) return;

    const tooltipEl = tooltipRef.current;
    const triggerEl = triggerRef.current;

    const tooltipRect = tooltipEl.getBoundingClientRect();
    const triggerRect = triggerEl.getBoundingClientRect();

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    if (tooltipRect.left < 0) {
      setAdjustedLeft(`${-1 * triggerRect.left}px`);
    }
    if (tooltipRect.right > screenWidth) {
      setAdjustedRight(`${triggerRect.right - (screenWidth - 28)}px`);
    }

    if (tooltipRect.top < 0) {
      setAdjustedTop("25px");
      setAdjustedBottom(null);
    } else if (tooltipRect.bottom > screenHeight) {
      setAdjustedTop("25px");
    }

    const handleClickOutside = (event: MouseEvent): void => {
      // permet de savoir s'il on a cliqué en dehors du tooltips
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setVisible(false);
      }
    };

    if (visible) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [visible]);

  return (
    <TooltipsContent
      data-testid="TooltipsContent"
      onClick={() => {
        setVisible(prev => !prev);
      }}
      ref={triggerRef}>
      <IconTooltipsStyled data-testid="IconTooltipsStyled" />
      <TooltipsMessage
        data-testid="TooltipsMessage"
        ref={tooltipRef}
        $isVisible={visible}
        $adjustedLeft={adjustedLeft}
        $adjustedRight={adjustedRight}
        $adjustedTop={adjustedTop}
        $adjustedBottom={adjustedBottom}
        onClick={e => e.stopPropagation()}>
        <p>{content}</p>
      </TooltipsMessage>
    </TooltipsContent>
  );
}
